// =====================================================
// COMPONENT: ContactInfoForm
// Description: Formulaire éditable pour les informations d'un contact
// =====================================================

import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { FormInput, FormTextarea, FormPhoneInput, FormSelect } from '../ui';
import type { Contact } from '../../types/contact';
import { COUNTRIES } from '../../types/school';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { useSchoolsSelect } from '../../hooks/useSchoolsSelect';
import { useOrigins } from '../../hooks/useOrigins';
import { EditContactProductsModal } from '../modals/EditContactProductsModal';

// Schéma pour l'édition des infos générales
const contactInfoSchema = z.object({
  first_name: z.string().min(1, 'Le prénom est requis').max(100, 'Le prénom est trop long'),
  last_name: z.string().min(1, 'Le nom est requis').max(100, 'Le nom est trop long'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  school_id: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  // Adresse
  address: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  postal_code: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  // Date de naissance
  birth_date: z.string().optional().nullable(),
  // Origine
  origin_id: z.string().optional().nullable(),
  origin_info: z.string().optional().nullable(),
  // Statut étudiant
  is_not_student: z.boolean().optional(),
  socio_professional_category: z.string().optional().nullable(),
  // Newsletter
  newsletter_opt_in: z.boolean().optional(),
});

type ContactInfoInput = z.infer<typeof contactInfoSchema>;

interface ContactInfoFormProps {
  contact: Contact;
  onSave: (data: ContactInfoInput | { product_ids: string[] }) => Promise<boolean>;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onSubmitRef?: (submitFn: () => void) => void;
}

export function ContactInfoForm({
  contact,
  onSave,
  onDirtyChange,
  onSubmittingChange,
  onSubmitRef,
}: ContactInfoFormProps) {
  const { products: allProducts } = useProductsSelect();
  const { schools } = useSchoolsSelect();
  const { origins } = useOrigins();
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    contact.products?.map((p) => p.id) || []
  );
  const [isEditProductsModalOpen, setIsEditProductsModalOpen] = useState(false);

  // Synchroniser selectedProducts quand le contact change (utiliser l'ID du contact pour détecter un rechargement)
  useEffect(() => {
    const newProductIds = contact.products?.map((p) => p.id) || [];
    setSelectedProducts(newProductIds);
  }, [contact.id, contact.products]);

  // Calculer les produits à afficher basés sur selectedProducts
  const displayedProducts = useMemo(() => {
    return allProducts.filter((product) => selectedProducts.includes(product.id));
  }, [allProducts, selectedProducts]);

  const form = useForm<ContactInfoInput>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone || '',
      school_id: contact.school_id || '',
      notes: contact.notes || '',
      // Adresse
      address: contact.address || '',
      city: contact.city || '',
      postal_code: contact.postal_code || '',
      country: contact.country || 'FRA',
      // Date de naissance
      birth_date: contact.birth_date || '',
      // Origine
      origin_id: contact.origin_id || '',
      origin_info: contact.origin_info || '',
      // Statut étudiant
      is_not_student: contact.is_not_student || false,
      socio_professional_category: contact.socio_professional_category || '',
      // Newsletter
      newsletter_opt_in: contact.newsletter_opt_in ?? true,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  // Observer les champs booléens pour le rendu conditionnel
  const isNotStudent = useWatch({ control, name: 'is_not_student' });
  const newsletterOptIn = useWatch({ control, name: 'newsletter_opt_in' });
  const selectedOriginId = useWatch({ control, name: 'origin_id' });

  const ORIGINS_WITH_INFO = ['salon_evenement', 'webinaire', 'autre'];
  const showOriginInfo = ORIGINS_WITH_INFO.includes(
    origins.find((o) => o.id === selectedOriginId)?.name ?? ''
  );

  // Fonction de soumission stable
  const onSubmit = useCallback(
    async (data: ContactInfoInput) => {
      // Convertir school_id vide en null pour éviter l'erreur UUID
      const submitData = {
        ...data,
        school_id: data.school_id || null,
        origin_id: data.origin_id || null,
        origin_info: data.origin_info || null,
      };
      const success = await onSave(submitData);
      if (success) {
        // Reset le formulaire avec les nouvelles valeurs pour remettre isDirty à false
        form.reset(data, { keepValues: true });
      }
    },
    [onSave, form]
  );

  // Réinitialiser le formulaire quand le contact change
  useEffect(() => {
    form.reset({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone || '',
      school_id: contact.school_id || '',
      notes: contact.notes || '',
      // Adresse
      address: contact.address || '',
      city: contact.city || '',
      postal_code: contact.postal_code || '',
      country: contact.country || 'FRA',
      // Date de naissance
      birth_date: contact.birth_date || '',
      // Origine
      origin_id: contact.origin_id || '',
      origin_info: contact.origin_info || '',
      // Statut étudiant
      is_not_student: contact.is_not_student || false,
      socio_professional_category: contact.socio_professional_category || '',
      // Newsletter
      newsletter_opt_in: contact.newsletter_opt_in ?? true,
    });
  }, [contact, form]);

  // Notifier le parent du changement de l'état dirty
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Notifier le parent du changement de l'état submitting
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  // Créer une fonction stable pour la soumission
  const handleFormSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  // Exposer la fonction de soumission au parent
  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(handleFormSubmit);
    }
  }, [onSubmitRef, handleFormSubmit]);

  // Sauvegarder uniquement les produits
  async function handleSaveProducts(productIds: string[]) {
    const success = await onSave({ product_ids: productIds });
    if (success) {
      setSelectedProducts(productIds);
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Notes */}
          <FormTextarea
            control={control}
            name="notes"
            label="Notes"
            placeholder="Ajouter des notes..."
            rows={4}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormInput control={control} name="last_name" label="Nom" required />
            <FormInput control={control} name="first_name" label="Prénom" required />
            <FormInput control={control} name="email" label="Email" type="email" required />
            <FormPhoneInput control={control} name="phone" label="Téléphone" />
          </div>

          {/* Checkbox Plus étudiant + Inscrit à la newsletter */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={isNotStudent ?? false}
                  onChange={(checked) =>
                    form.setValue('is_not_student', checked, { shouldDirty: true })
                  }
                />
                <span
                  className="text-sm text-zinc-900 cursor-pointer"
                  onClick={() =>
                    form.setValue('is_not_student', !isNotStudent, { shouldDirty: true })
                  }
                >
                  Plus étudiant
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={newsletterOptIn ?? true}
                  onChange={(checked) =>
                    form.setValue('newsletter_opt_in', checked, { shouldDirty: true })
                  }
                />
                <span
                  className="text-sm text-zinc-900 cursor-pointer"
                  onClick={() =>
                    form.setValue('newsletter_opt_in', !(newsletterOptIn ?? true), {
                      shouldDirty: true,
                    })
                  }
                >
                  Inscrit à la newsletter
                </span>
              </div>
            </div>

            {isNotStudent ? (
              <FormInput
                control={control}
                name="socio_professional_category"
                label="Catégorie socio-professionnelle"
                placeholder="Ex: Cadre, Artisan, Profession libérale..."
              />
            ) : (
              <FormSelect
                control={control}
                name="school_id"
                label="École"
                placeholder="Sélectionner une école"
                options={[
                  { value: '', label: 'Aucune' },
                  ...schools.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
            )}
          </div>

          {/* Section Adresse */}
          <div className="flex flex-col gap-4">
            <FormInput
              control={control}
              name="address"
              label="Adresse"
              placeholder="Ex: 12 rue de la Paix"
            />
            <div className="grid grid-cols-3 gap-4">
              <FormInput control={control} name="city" label="Ville" placeholder="Paris" />
              <FormInput
                control={control}
                name="postal_code"
                label="Code Postal"
                placeholder="75001"
              />
              <FormSelect
                control={control}
                name="country"
                label="Pays"
                options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={control}
                name="birth_date"
                label="Date de naissance"
                type="date"
              />
              <FormSelect
                control={control}
                name="origin_id"
                label="Origine"
                placeholder="Aucune origine"
                options={[
                  { value: '', label: 'Aucune origine' },
                  ...origins.map((o) => ({ value: o.id, label: o.label })),
                ]}
              />
            </div>
            {showOriginInfo && (
              <FormInput
                control={control}
                name="origin_info"
                label="Complément info origine"
                placeholder="Ex: Salon du Tourisme de Paris, Webinaire Rose Trip 2026..."
              />
            )}
          </div>

          {/* Produits associés - Badges avec bouton d'édition */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-zinc-900">Produits associés</label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditProductsModalOpen(true)}
                className="h-8 px-2"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[2rem] items-center">
              {displayedProducts.length === 0 ? (
                <span className="text-sm text-zinc-500 italic">Aucun produit associé</span>
              ) : (
                displayedProducts.map((product) => (
                  <Badge key={product.id} variant="outline">
                    {product.name}
                  </Badge>
                ))
              )}
            </div>
          </div>
        </form>
      </FormProvider>

      {/* Modal d'édition des produits */}
      <EditContactProductsModal
        isOpen={isEditProductsModalOpen}
        onClose={() => setIsEditProductsModalOpen(false)}
        contactId={contact.id}
        currentProducts={selectedProducts}
        onSave={handleSaveProducts}
      />
    </>
  );
}
